import {
  Activity, BadgeRussianRuble, Boxes, CheckCircle2, CircleDollarSign, CreditCard,
  Edit3, PackagePlus, RefreshCw, Search, ShoppingBag, SlidersHorizontal, Trash2,
  UsersRound, WalletCards
} from 'lucide-react';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api, patch, post } from '../api';
import { useAuth } from '../App';
import Modal from '../components/Modal';
import PageMotion from '../components/PageMotion';
import StatusBadge from '../components/StatusBadge';
import { date, money, number, shortDate } from '../format';
import type { Order, Plan, Transaction, User } from '../types';

type AdminOrder = Order & { user_name: string; user_email: string };
type AdminUser = User & { orders_count: number; total_spent: number; total_reach: number };
type Topup = {
  id: number;
  user_name: string;
  user_email: string;
  amount: number;
  fee_amount: number;
  payable_amount: number;
  status: string;
  payment_id?: string;
  created_at: string;
};
type Summary = {
  orders: { total: number; revenue: number; active: number; completed: number; reach: number };
  users: { total: number; balances: number };
  topups: { paid_count: number; paid_amount: number; pending: number };
  recent_orders: AdminOrder[];
};
type UserDetail = { user: User; orders: Order[]; transactions: Transaction[] };
type YmStatus = { configured: boolean; reachable: boolean; status?: string; error?: string };
type AdminTab = 'overview' | 'orders' | 'users' | 'plans' | 'topups';

const tabs: Array<{ id: AdminTab; label: string; icon: typeof Activity }> = [
  { id: 'overview', label: 'Обзор', icon: Activity },
  { id: 'orders', label: 'Заказы', icon: ShoppingBag },
  { id: 'users', label: 'Пользователи', icon: UsersRound },
  { id: 'plans', label: 'Тарифы', icon: SlidersHorizontal },
  { id: 'topups', label: 'Пополнения', icon: CreditCard }
];

const categoryLabels: Record<string, string> = {
  plays: 'Прослушивания',
  listeners: 'Слушатели',
  album_likes: 'Лайки альбома',
  artist_likes: 'Лайки артиста',
  presaves: 'Пресейвы'
};

function messageOf(reason: unknown, fallback: string) {
  return reason instanceof Error ? reason.message : fallback;
}

function AdminOrdersTable({
  orders,
  onUpdate
}: {
  orders: AdminOrder[];
  onUpdate: (id: number, value: Partial<Order>) => Promise<void>;
}) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr><th>Заказ</th><th>Клиент</th><th>Сумма</th><th>Прогресс</th><th>Статус</th></tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td><strong>#{order.id} · {order.title}</strong><small>{shortDate(order.created_at)}{order.remote_id ? ` · YM #${order.remote_id}` : ''}</small></td>
              <td><strong>{order.user_name}</strong><small>{order.user_email}</small></td>
              <td><strong>{money(order.amount)}</strong><small>{number(order.quantity)} ед.</small></td>
              <td>
                <div className="admin-progress">
                  <span><i style={{ width: `${order.progress}%` }} /></span>
                  <input
                    key={order.progress}
                    aria-label={`Прогресс заказа ${order.id}`}
                    type="number"
                    min="0"
                    max="100"
                    defaultValue={order.progress}
                    onBlur={(event) => onUpdate(order.id, { progress: Number(event.target.value) })}
                  />
                </div>
              </td>
              <td>
                <select
                  aria-label={`Статус заказа ${order.id}`}
                  value={order.status}
                  onChange={(event) => onUpdate(order.id, { status: event.target.value as Order['status'] })}
                >
                  <option value="pending">В очереди</option>
                  <option value="processing">Выполняется</option>
                  <option value="completed">Завершён</option>
                  <option value="failed">Ошибка</option>
                  <option value="cancelled">Отменён</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!orders.length && <div className="admin-empty">Заказов пока нет</div>}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<AdminTab>('overview');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [topups, setTopups] = useState<Topup[]>([]);
  const [ymStatus, setYmStatus] = useState<YmStatus>({ configured: false, reachable: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [userOpen, setUserOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    if (!user?.is_admin) return;
    setLoading(true);
    setError('');
    try {
      const [summaryData, orderData, userData, planData, topupData, ymData] = await Promise.all([
        api<Summary>('/admin/summary'),
        api<{ orders: AdminOrder[] }>('/admin/orders'),
        api<{ users: AdminUser[] }>('/admin/users'),
        api<{ plans: Plan[] }>('/admin/plans'),
        api<{ topups: Topup[] }>('/admin/topups'),
        api<YmStatus>('/admin/ym/status').catch(() => ({ configured: true, reachable: false, status: 'error' }))
      ]);
      setSummary(summaryData);
      setOrders(orderData.orders);
      setUsers(userData.users);
      setPlans(planData.plans);
      setTopups(topupData.topups);
      setYmStatus(ymData);
    } catch (reason) {
      setError(messageOf(reason, 'Не удалось загрузить админ-панель'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadAll(); }, [user?.is_admin]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((item) => `${item.name} ${item.email}`.toLowerCase().includes(query));
  }, [search, users]);

  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;

  const updateOrder = async (id: number, value: Partial<Order>) => {
    try {
      const result = await patch<{ order: Order }>(`/admin/orders/${id}`, value);
      const merge = (item: AdminOrder) => item.id === id ? { ...item, ...result.order } : item;
      setOrders((items) => items.map(merge));
      setSummary((current) => current ? { ...current, recent_orders: current.recent_orders.map(merge) } : current);
    } catch (reason) {
      setError(messageOf(reason, 'Не удалось обновить заказ'));
    }
  };

  const openUser = async (id: number) => {
    setUserOpen(true);
    setSelectedUser(null);
    setError('');
    try {
      setSelectedUser(await api<UserDetail>(`/admin/users/${id}`));
    } catch (reason) {
      setError(messageOf(reason, 'Не удалось загрузить пользователя'));
    }
  };

  const adjustBalance = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedUser) return;
    const form = new FormData(event.currentTarget);
    setSaving(true);
    setError('');
    try {
      const result = await post<{ user: User }>(`/admin/users/${selectedUser.user.id}/balance`, {
        amount: Number(form.get('amount')),
        title: form.get('title')
      });
      const difference = result.user.balance - selectedUser.user.balance;
      setSelectedUser({ ...selectedUser, user: result.user });
      setUsers((items) => items.map((item) => item.id === result.user.id ? { ...item, balance: result.user.balance } : item));
      setSummary((current) => current ? {
        ...current,
        users: { ...current.users, balances: current.users.balances + difference }
      } : current);
      event.currentTarget.reset();
    } catch (reason) {
      setError(messageOf(reason, 'Не удалось изменить баланс'));
    } finally {
      setSaving(false);
    }
  };

  const savePlan = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      category: form.get('category'),
      title: form.get('title'),
      description: form.get('description'),
      quantity: Number(form.get('quantity')),
      price: Number(form.get('price')),
      unit: form.get('unit'),
      accent: form.get('accent'),
      sort_order: Number(form.get('sort_order')),
      is_active: form.get('is_active') === 'on'
    };
    setSaving(true);
    setError('');
    try {
      if (editingPlan) await patch(`/admin/plans/${editingPlan.id}`, payload);
      else await post('/admin/plans', payload);
      const result = await api<{ plans: Plan[] }>('/admin/plans');
      setPlans(result.plans);
      setPlanOpen(false);
      setEditingPlan(null);
    } catch (reason) {
      setError(messageOf(reason, 'Не удалось сохранить тариф'));
    } finally {
      setSaving(false);
    }
  };

  const disablePlan = async (plan: Plan) => {
    try {
      await api(`/admin/plans/${plan.id}`, { method: 'DELETE' });
      setPlans((items) => items.map((item) => item.id === plan.id ? { ...item, is_active: 0 } : item));
    } catch (reason) {
      setError(messageOf(reason, 'Не удалось отключить тариф'));
    }
  };

  return (
    <PageMotion>
      <header className="page-header admin-page-header">
        <div>
          <span className="eyebrow">Управление HOLLA MARKET</span>
          <h1>Админ панель</h1>
          <p>Заказы, пользователи, тарифы и платежи в одном месте.</p>
        </div>
        <button className="icon-button" onClick={() => void loadAll()} aria-label="Обновить админ-панель">
          <RefreshCw size={18} className={loading ? 'spin-icon' : ''} />
        </button>
      </header>

      {error && <div className="inline-error admin-error"><span>{error}</span><button onClick={() => setError('')}>Закрыть</button></div>}

      <nav className="admin-tabs">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
            <Icon size={17} /><span>{label}</span>
            {id === 'orders' && summary?.orders.active ? <b>{summary.orders.active}</b> : null}
            {id === 'topups' && summary?.topups.pending ? <b>{summary.topups.pending}</b> : null}
          </button>
        ))}
      </nav>

      {loading && !summary ? (
        <div className="admin-loading"><span className="spinner" /> Загружаем данные</div>
      ) : (
        <>
          {tab === 'overview' && summary && (
            <section className="admin-view">
              <div className="admin-kpis">
                <article><span className="metric-icon tone-orange"><CircleDollarSign size={20} /></span><div><small>Оборот заказов</small><strong>{money(summary.orders.revenue)}</strong><p>{summary.orders.total} заказов</p></div></article>
                <article><span className="metric-icon tone-blue"><Activity size={20} /></span><div><small>В работе</small><strong>{number(summary.orders.active)}</strong><p>{number(summary.orders.reach)} действий</p></div></article>
                <article><span className="metric-icon tone-green"><UsersRound size={20} /></span><div><small>Пользователи</small><strong>{number(summary.users.total)}</strong><p>{money(summary.users.balances)} на балансах</p></div></article>
                <article><span className="metric-icon tone-violet"><WalletCards size={20} /></span><div><small>Пополнения</small><strong>{money(summary.topups.paid_amount)}</strong><p>{summary.topups.paid_count} успешных</p></div></article>
              </div>
              <div className="admin-overview-grid">
                <section className="content-section">
                  <div className="section-title"><div><span className="eyebrow">Последние</span><h2>Новые заказы</h2></div><button className="text-button" onClick={() => setTab('orders')}>Все заказы</button></div>
                  <AdminOrdersTable orders={summary.recent_orders.slice(0, 6)} onUpdate={updateOrder} />
                </section>
                <aside className="content-section admin-health">
                  <span className="eyebrow">Система</span><h2>Состояние магазина</h2>
                  <div><span><i className="health-green" /> Кабинет и API</span><b>Работают</b></div>
                  <div><span><i className="health-green" /> T-Bank</span><b>Подключён</b></div>
                  <div><span><i className={ymStatus.reachable ? 'health-green' : 'health-orange'} /> YM-GO API</span><b>{ymStatus.reachable ? 'На связи' : ymStatus.configured ? 'Недоступен' : 'Не настроен'}</b></div>
                  <div><span><i className={summary.topups.pending ? 'health-orange' : 'health-green'} /> Ожидают оплаты</span><b>{summary.topups.pending}</b></div>
                  <div><span><i className="health-green" /> Выполнено заказов</span><b>{summary.orders.completed}</b></div>
                </aside>
              </div>
            </section>
          )}

          {tab === 'orders' && (
            <section className="admin-view">
              <div className="admin-view-head"><div><h2>Все заказы</h2><p>{orders.length} записей · изменения применяются сразу</p></div></div>
              <AdminOrdersTable orders={orders} onUpdate={updateOrder} />
            </section>
          )}

          {tab === 'users' && (
            <section className="admin-view">
              <div className="admin-view-head">
                <div><h2>Пользователи</h2><p>Баланс, заказы и история операций</p></div>
                <label className="admin-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Имя или почта" /></label>
              </div>
              <div className="admin-users-list">
                {filteredUsers.map((item) => (
                  <button key={item.id} className="admin-user-row" onClick={() => void openUser(item.id)}>
                    <span className="admin-user-avatar">{item.name.slice(0, 1).toUpperCase()}</span>
                    <div><strong>{item.name}{item.is_admin && <em>Админ</em>}</strong><small>{item.email}</small></div>
                    <div><strong>{money(item.balance)}</strong><small>{item.orders_count} заказов · {money(item.total_spent)}</small></div>
                    <Edit3 size={17} />
                  </button>
                ))}
              </div>
            </section>
          )}

          {tab === 'plans' && (
            <section className="admin-view">
              <div className="admin-view-head">
                <div><h2>Тарифы</h2><p>Названия, объём, цена и доступность</p></div>
                <button className="primary-button" onClick={() => { setEditingPlan(null); setPlanOpen(true); }}><PackagePlus size={17} /> Создать тариф</button>
              </div>
              <div className="admin-plans-grid">
                {plans.map((plan) => (
                  <article key={plan.id} className={Number(plan.is_active ?? 1) ? '' : 'disabled'}>
                    <div className="admin-plan-top"><span className={`service-icon tone-${plan.accent}`}><Boxes size={18} /></span><div><small>{categoryLabels[plan.category] || plan.category}</small><h3>{plan.title}</h3></div></div>
                    <div className="admin-plan-numbers"><span><small>Объём</small><strong>{number(plan.quantity)}</strong></span><span><small>Цена</small><strong>{money(plan.price)}</strong></span></div>
                    <p>{plan.description}</p>
                    <div className="admin-plan-actions"><button onClick={() => { setEditingPlan(plan); setPlanOpen(true); }}><Edit3 size={15} /> Изменить</button><button className="danger" onClick={() => void disablePlan(plan)} aria-label={`Отключить ${plan.title}`}><Trash2 size={15} /></button></div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {tab === 'topups' && (
            <section className="admin-view">
              <div className="admin-view-head"><div><h2>Пополнения</h2><p>Счета и фактически зачисленные платежи</p></div></div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Счёт</th><th>Пользователь</th><th>На баланс</th><th>К оплате</th><th>Статус</th></tr></thead>
                  <tbody>{topups.map((topup) => (
                    <tr key={topup.id}>
                      <td><strong>#{topup.id}</strong><small>{shortDate(topup.created_at)}</small></td>
                      <td><strong>{topup.user_name}</strong><small>{topup.user_email}</small></td>
                      <td><strong>{money(topup.amount)}</strong><small>Комиссия {money(topup.fee_amount)}</small></td>
                      <td><strong>{money(topup.payable_amount)}</strong><small>{topup.payment_id ? `ID ${topup.payment_id}` : 'Без Payment ID'}</small></td>
                      <td><span className={`admin-payment-status payment-${topup.status}`}>{topup.status}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      <Modal open={userOpen} title={selectedUser?.user.name || 'Пользователь'} eyebrow="Управление аккаунтом" onClose={() => { setUserOpen(false); setSelectedUser(null); setError(''); }}>
        {!selectedUser ? <div className="loading-state"><span className="spinner" /> Загрузка</div> : (
          <div className="admin-user-detail">
            <div className="user-detail-head"><span className="admin-user-avatar large">{selectedUser.user.name.slice(0, 1).toUpperCase()}</span><div><h3>{selectedUser.user.name}</h3><p>{selectedUser.user.email}</p><small>Регистрация: {date(selectedUser.user.created_at)}</small></div><strong>{money(selectedUser.user.balance)}</strong></div>
            <form className="balance-adjust-form" onSubmit={adjustBalance}>
              <div className="form-grid"><label>Изменение баланса<input name="amount" type="number" step="1" placeholder="Например, 500 или -200" required /></label><label>Комментарий<input name="title" placeholder="Причина начисления" /></label></div>
              <button className="primary-button full" disabled={saving}><BadgeRussianRuble size={17} /> Применить</button>
            </form>
            <div className="user-detail-section"><h3>Последние заказы</h3>{selectedUser.orders.length ? selectedUser.orders.slice(0, 8).map((order) => <div className="detail-line" key={order.id}><div><strong>#{order.id} · {order.title}</strong><small>{shortDate(order.created_at)}</small></div><StatusBadge status={order.status} /></div>) : <p>Заказов пока нет</p>}</div>
            <div className="user-detail-section"><h3>Операции</h3>{selectedUser.transactions.length ? selectedUser.transactions.slice(0, 8).map((item) => <div className="detail-line" key={item.id}><div><strong>{item.title}</strong><small>{shortDate(item.created_at)}</small></div><b className={item.amount > 0 ? 'positive' : ''}>{item.amount > 0 ? '+' : ''}{money(item.amount)}</b></div>) : <p>Операций пока нет</p>}</div>
          </div>
        )}
      </Modal>

      <Modal open={planOpen} title={editingPlan ? 'Изменить тариф' : 'Новый тариф'} eyebrow="Каталог услуг" onClose={() => { setPlanOpen(false); setEditingPlan(null); setError(''); }}>
        <form className="admin-plan-form" onSubmit={savePlan} key={editingPlan?.id || 'new'}>
          <div className="form-grid">
            <label>Категория<select name="category" defaultValue={editingPlan?.category || 'plays'}><option value="plays">Прослушивания</option><option value="listeners">Слушатели</option><option value="album_likes">Лайки альбома</option><option value="artist_likes">Лайки артиста</option><option value="presaves">Пресейвы</option></select></label>
            <label>Цвет<select name="accent" defaultValue={editingPlan?.accent || 'orange'}><option value="orange">Оранжевый</option><option value="cyan">Голубой</option><option value="pink">Розовый</option><option value="violet">Фиолетовый</option><option value="green">Зелёный</option></select></label>
          </div>
          <label>Название<input name="title" defaultValue={editingPlan?.title || ''} required placeholder="1 000 прослушиваний" /></label>
          <label>Описание<input name="description" defaultValue={editingPlan?.description || ''} placeholder="Короткое описание тарифа" /></label>
          <div className="form-grid"><label>Количество<input name="quantity" type="number" min="1" defaultValue={editingPlan?.quantity || 1000} required /></label><label>Цена, ₽<input name="price" type="number" min="0" step="0.01" defaultValue={editingPlan?.price || 900} required /></label></div>
          <div className="form-grid"><label>Единица<input name="unit" defaultValue={editingPlan?.unit || 'прослушиваний'} required /></label><label>Сортировка<input name="sort_order" type="number" defaultValue={editingPlan?.sort_order || 0} /></label></div>
          <label className="admin-check"><input name="is_active" type="checkbox" defaultChecked={Number(editingPlan?.is_active ?? 1) === 1} /><span><CheckCircle2 size={17} /> Тариф доступен пользователям</span></label>
          <button className="primary-button full" disabled={saving}>{saving ? <span className="spinner" /> : editingPlan ? 'Сохранить изменения' : 'Создать тариф'}</button>
        </form>
      </Modal>
    </PageMotion>
  );
}
